import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { ContentfulConfigService } from '@scalefast/config-angular';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { ContentfulPagePathContent } from 'src/app/core/interfaces/Contentful/PagePath/ContentfulPagePathContent';
import { PrefetchHandler } from 'src/app/core/interfaces/prefetch-handler.interface';
import { getContentfulConfig } from '../helpers/contentful.helper';
import { ContentfulLiteService } from './contentful-lite.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class ContentfulPrefetchService implements PrefetchHandler<ContentfulPagePathContent> {
  #contentfulLiteService = inject(ContentfulLiteService);
  #contentfulConfigService = inject(ContentfulConfigService);
  #logger = inject(GenericLogger);
  #transferState = inject(TransferState);
  #platformID = inject(PLATFORM_ID);
  #contentfulLocale: string;
  #prefetchedPages: Record<string, Promise<ContentfulPagePathContent | undefined>> = {};
  #serializedPages: Record<string, ContentfulPagePathContent | undefined> = {};
  readonly #CONTENTFUL_PREFETCH = makeStateKey<Record<string, ContentfulPagePathContent>>('contentfulPrefetch');

  /**
   * Specialized PrefetchService for Contentful pages.
   * This service is responsible for prefetching and caching Contentful pages to improve performance and reduce load times.
   * It uses the ContentfulLiteService to fetch page data and stores the prefetched pages in a cache.
   * The service also handles serialization and deserialization of the prefetched pages for server-side rendering.
   * @implements {PrefetchHandler<ContentfulPagePathContent>}
   */
  constructor() {
    const contentfulConfig = getContentfulConfig(this.#contentfulConfigService);
    this.#contentfulLocale = contentfulConfig.locale;
    if (isPlatformBrowser(this.#platformID)) {
      const data = this.#transferState.get(this.#CONTENTFUL_PREFETCH, {});
      this.#prefetchedPages = Object.fromEntries(Object.entries(data).map(([key, value]) => [key, Promise.resolve(value)]));
    }
    // Save the pages in the transferState. This action is made at the end of Angular process.
    this.#transferState.onSerialize(this.#CONTENTFUL_PREFETCH, () => this.#serializedPages);
  }

  /**
   * Prefetches a Contentful page based on the given route. We use the route to generate a cache key for the page based in the locale.
   * TODO: The responsability of the cache key generation should be probably moved to the setPageInCache method, but we need a way to
   * generate the proper slug needed to fetch the page from Contentful.
   * @param {string} route - The route of the page to prefetch. This comes without the locale, so we need to generate the final slug to make the request to Contentful.
   * @returns {Promise<void>} A promise that resolves when the page has been prefetched.
   */
  async prefetchPage(route: string): Promise<void> {
    const cacheKey = this.#getCacheKey(route);
    const contentfulPagePromise = this.#contentfulLiteService.resolveContentfulPageByPath(cacheKey);
    this.setPageInCache(cacheKey, contentfulPagePromise);
  }

  /**
   * Caches the prefetched page response.
   * @param {string} cacheKey - The cache key for the page.
   * @param {Promise<ContentfulPagePathContent>} response - The promise of the page content to cache.
   */
  setPageInCache(cacheKey: string, response: Promise<ContentfulPagePathContent>): void {
    this.#prefetchedPages[cacheKey] = response.then((data: any) => data);
    response.then((data: any) => (this.#serializedPages[cacheKey] = data));
  }

  /**
   * Generates a cache key for the given route.
   * @param {string} route - The route of the page.
   * @returns {string} The generated cache key that includes the locale.
   */
  #getCacheKey(route: string): string {
    return '/' + this.#contentfulLocale.toLowerCase() + route;
  }

  /**
   * Retrieves a prefetched page from the cache.
   * @param {string} route - The route of the page to retrieve.
   * @returns {Promise<ContentfulPagePathContent | undefined>} A promise that resolves to the prefetched page content, or undefined if not found.
   */
  getPrefetchedPage(route: string): Promise<ContentfulPagePathContent | undefined> {
    this.#logger.debug({ msg: 'ContentfulPrefetchService: prefetched data is being used', route });
    return this.#prefetchedPages[route];
  }

  /**
   * This function is currently a no-op and always returns false.
   * The actual check for prefetched data is handled in the principal service BuilderPrefetchService.
   * In case the principal service returns false, the Contentful will return false as well,
   * so we force to prefetch the content.
   */
  isAlreadyPrefetched(route: string, params: any): boolean {
    return false;
  }
}
